import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  CircularProgress,
  FormControl,
  Select,
  MenuItem as MuiMenuItem,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Notifications,
  AccountBalance,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  SimCard as SimCardIcon,
  Receipt as ReceiptIcon,
  AdminPanelSettings as AdminIcon,
  Group as GroupIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, updateUserPreferences } from "../store/slices/authSlice";
import { fetchCreditBalance } from "../store/slices/creditSlice";
import { setCurrentProfile, setProfiles } from "../store/slices/profilesSlice";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import axios from "axios";
import ProfileSelector from "../components/ProfileSelector";

const Navbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { balance, loading } = useSelector((state) => state.credits);
  const { token } = useSelector((state) => state.auth);
  const { profiles, currentProfile } = useSelector((state) => state.profiles);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (user) {
      dispatch(fetchCreditBalance());
    }
  }, [user, dispatch]);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const response = await axios.get("/api/profiles", {
          headers: { Authorization: `Bearer ${token}` },
        });
        dispatch(setProfiles(response.data));
      } catch (error) {
        console.error("Error fetching profiles:", error);
      }
    };
    if (token) fetchProfiles();
  }, [token, dispatch]);

  useEffect(() => {
    if (user?.language) {
      i18n.changeLanguage(user.language);
    }
  }, [user]);

  useEffect(() => {
    const savedProfileId = localStorage.getItem("currentProfile");
    if (savedProfileId && profiles.length > 0) {
      dispatch(setCurrentProfile(savedProfileId));
    }
  }, [dispatch, profiles]);

  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    i18n.changeLanguage(newLanguage);
    localStorage.setItem("i18nextLng", newLanguage);
    if (user) {
      dispatch(updateUserPreferences({ language: newLanguage }))
        .unwrap()
        .catch((error) => {
          console.error("Failed to update language preference:", error);
        });
    }
  };

  useEffect(() => {
    const savedLanguage =
      localStorage.getItem("i18nextLng") || user?.language || "en";
    i18n.changeLanguage(savedLanguage);
  }, [user?.language]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileChange = (profileId) => {
    localStorage.setItem("currentProfile", profileId);
    dispatch(setCurrentProfile(profileId));
  };

  const menuItems = [
    { title: t("menu.dashboard"), path: "/", icon: DashboardIcon },
    { title: t("menu.purchaseESIM"), path: "/esim", icon: SimCardIcon },
    { title: t("menu.esimOrders"), path: "/orders", icon: SimCardIcon },
    { title: t("menu.transactions"), path: "/transactions", icon: ReceiptIcon },
  ];

  const adminMenuItems = [
    { title: t("menu.adminDashboard"), path: "/admin", icon: AdminIcon },
    { title: t("menu.resellers"), path: "/admin/resellers", icon: GroupIcon },
    {
      title: t("menu.esimOrders"),
      path: "/admin/esim-orders",
      icon: SimCardIcon,
    },
    {
      title: t("menu.transactions"),
      path: "/admin/transactions",
      icon: ReceiptIcon,
    },
    {
      title: t("menu.emailTemplates"),
      path: "/admin/email-templates",
      icon: EmailIcon,
    },
    { title: t("menu.settings"), path: "/admin/settings", icon: SettingsIcon },
  ];

  return (
    <>
      <AppBar position="fixed">
        <Toolbar>
          {user ? (
            isMobile ? (
              <>
                <Box sx={{ flexGrow: 1 }}>
                  <img
                    src="/logo-white.webp"
                    alt="Logo"
                    style={{ maxWidth: 150 }}
                  />
                </Box>
                <IconButton
                  color="inherit"
                  edge="end"
                  onClick={toggleMobileMenu}
                >
                  <MenuIcon />
                </IconButton>
              </>
            ) : (
              <>
                <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <img
                    src="/logo-white.webp"
                    alt="Logo"
                    style={{ maxWidth: 200 }}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 2,
                  }}
                >
                  <FormControl variant="standard" sx={{ minWidth: 80 }}>
                    <Select
                      value={i18n.language}
                      onChange={handleLanguageChange}
                      defaultValue="en"
                    >
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="mkd">Македонски</MenuItem>
                      <MenuItem value="tr">Turkce</MenuItem>
                      <MenuItem value="al">Albanian</MenuItem>
                      <MenuItem value="es">Español</MenuItem>
                    </Select>
                  </FormControl>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccountBalance sx={{ mr: 1 }} />
                    {loading ? (
                      <CircularProgress size={30} />
                    ) : (
                      <Typography variant="h5">
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                        }).format(balance)}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    color="inherit"
                    aria-label={t("common.notifications")}
                  >
                    <Notifications />
                  </IconButton>
                  <IconButton size="large" color="inherit" onClick={handleMenu}>
                    <Avatar sx={{ width: 32, height: 32 }} src={user?.logoUrl}>
                      {user?.username?.charAt(0).toUpperCase()}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleClose();
                        navigate("/profile");
                      }}
                    >
                      {t("common.menu.profile")}
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      {t("common.menu.logout")}
                    </MenuItem>
                  </Menu>
                </Box>
              </>
            )
          ) : isMobile ? (
            <>
              <Box sx={{ flexGrow: 1 }}>
                <img
                  src="/logo-white.webp"
                  alt="Logo"
                  style={{ maxWidth: 150 }}
                />
              </Box>
              <IconButton color="inherit" edge="end" onClick={toggleMobileMenu}>
                <MenuIcon />
              </IconButton>
            </>
          ) : (
            <>
              <Box sx={{ flexGrow: 1 }}>
                <img
                  src="/logo-white.webp"
                  alt="Logo"
                  style={{ maxWidth: 150 }}
                />
              </Box>
              <FormControl variant="standard" sx={{ minWidth: 80 }}>
                <Select value={i18n.language} onChange={handleLanguageChange}>
                  <MuiMenuItem value="en">English</MuiMenuItem>
                  <MuiMenuItem value="mkd">Македонски</MuiMenuItem>
                  <MuiMenuItem value="tr">Turkce</MuiMenuItem>
                  <MuiMenuItem value="al">Albanian</MuiMenuItem>
                  <MuiMenuItem value="es">Español</MuiMenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Drawer anchor="right" open={mobileOpen} onClose={toggleMobileMenu}>
        <Box sx={{ width: 250, padding: 2 }}>
          {user ? (
            <>
              <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    color: "background.paper",
                    width: 40,
                    height: 40,
                  }}
                >
                  {user?.username?.charAt(0)?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" noWrap>
                    {user?.username}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {user?.role}
                  </Typography>
                </Box>
              </Box>
              <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
                <Select value={i18n.language} onChange={handleLanguageChange}>
                  <MuiMenuItem value="en">English</MuiMenuItem>
                  <MuiMenuItem value="mkd">Македонски</MuiMenuItem>
                  <MuiMenuItem value="tr">Turkce</MuiMenuItem>
                  <MuiMenuItem value="al">Albanian</MuiMenuItem>
                  <MuiMenuItem value="es">Español</MuiMenuItem>
                </Select>
              </FormControl>
              <List component="nav" sx={{ px: 1 }}>
                {menuItems.map((item) => (
                  <ListItem
                    button
                    key={item.path}
                    selected={location.pathname === item.path}
                    onClick={() => {
                      toggleMobileMenu();
                      navigate(item.path);
                    }}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      "& .MuiListItemIcon-root": { color: "inherit" },
                    }}
                  >
                    <ListItemIcon>
                      <item.icon />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: "0.875rem",
                        fontWeight: location.pathname === item.path ? 600 : 400,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
              <ListItem
                button
                selected={location.pathname === "/profile"}
                onClick={() => {
                  toggleMobileMenu();
                  navigate("/profile");
                }}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  "& .MuiListItemIcon-root": { color: "inherit" },
                }}
              >
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t("menu.profile")}
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    fontWeight: location.pathname === "/profile" ? 600 : 400,
                  }}
                />
              </ListItem>
              {profiles && profiles.length > 1 && (
                <Box sx={{ pl: 6, pr: 2, mb: 2 }}>
                  <ProfileSelector
                    profiles={profiles}
                    currentProfile={currentProfile}
                    onProfileChange={handleProfileChange}
                  />
                </Box>
              )}
              {user?.role === "admin" && (
                <>
                  <Divider sx={{ mt: 2 }} />
                  <Typography
                    variant="overline"
                    sx={{ px: 2, py: 1, display: "block" }}
                  >
                    {t("menu.adminArea")}
                  </Typography>
                  <List>
                    {adminMenuItems.map((item) => (
                      <ListItem
                        button
                        key={item.path}
                        selected={location.pathname === item.path}
                        onClick={() => {
                          toggleMobileMenu();
                          navigate(item.path);
                        }}
                        sx={{
                          "&.Mui-selected": {
                            bgcolor: "primary.light",
                            "&:hover": {
                              bgcolor: "primary.light",
                            },
                          },
                        }}
                      >
                        <ListItemIcon>
                          <item.icon />
                        </ListItemIcon>
                        <ListItemText primary={item.title} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
              <ListItem
                button
                onClick={() => {
                  toggleMobileMenu();
                  handleLogout();
                }}
              >
                <ListItemText primary={t("common.menu.logout")} />
              </ListItem>
            </>
          ) : (
            <FormControl variant="standard" fullWidth>
              <Select value={i18n.language} onChange={handleLanguageChange}>
                <MuiMenuItem value="en">English</MuiMenuItem>
                <MuiMenuItem value="mkd">Македонски</MuiMenuItem>
                <MuiMenuItem value="tr">Turkce</MuiMenuItem>
                <MuiMenuItem value="al">Albanian</MuiMenuItem>
                <MuiMenuItem value="es">Español</MuiMenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
